<template>
    <div class="flex min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      <!-- Sidebar -->
      <HrSidebar :isOpen="isSidebarOpen" />
  
      <!-- Main Content -->
      <main 
        class="flex-1 overflow-x-hidden transition-all duration-300"
        :class="{'ml-64': isSidebarOpen, 'ml-0': !isSidebarOpen}"
      >
        <!-- Navbar -->
             <header 
        class="fixed top-0 z-50 w-full bg-white dark:bg-gray-800 shadow-sm transition-all duration-300"
      >
        <HrTopbar 
          :page-title="currentPageTitle"
          :is-sidebar-open="isSidebarOpen"
          :is-dark-mode="isDarkMode"
          @toggle-sidebar="toggleSidebar"
          @toggle-dark-mode="toggleDarkMode"
        />
      </header>

      <div class="max-w-7xl mx-auto p-6 bg-white dark:bg-gray-800 rounded-lg shadow-md mt-6 mb-10 mt-[100px]">
        <div class="flex items-center justify-between mb-8">
          <h1 class="text-2xl font-bold text-[#035a76] dark:text-[#4db3ff]">
            Edit Employee Profile
          </h1>
          <button
            @click="goBack"
            class="px-5 py-2.5 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition"
          >
            ← Back to Profile
          </button>
        </div>

        <!-- Loading / Error -->
        <div v-if="loading" class="text-center py-12 text-gray-600 dark:text-gray-400">
          Loading employee data...
        </div>
        <div v-else-if="error" class="bg-red-50 border-l-4 border-red-500 text-red-700 p-6 rounded-lg mb-6">
          {{ error }}
        </div>

        <form v-else @submit.prevent="saveProfile" novalidate class="space-y-10">
          <!-- 1. Personal Information -->
          <section>
            <h2 class="text-xl font-semibold border-b pb-2 mb-6 text-[#035a76] dark:text-[#4db3ff]">
              Personal Information
            </h2>
            <div class="space-y-6">
              <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    First Name <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="form.personal.first_name"
                    type="text"
                    required
                    class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76] dark:bg-gray-700 dark:text-gray-100"
                    :class="{ 'border-red-500': errors.personal?.first_name }"
                  />
                  <p v-if="errors.personal?.first_name" class="text-red-500 text-sm mt-1">
                    {{ errors.personal.first_name }}
                  </p>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Middle Name</label>
                  <input
                    v-model="form.personal.middle_name"
                    type="text"
                    class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76] dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Last Name <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="form.personal.last_name"
                    type="text"
                    required
                    class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76] dark:bg-gray-700 dark:text-gray-100"
                    :class="{ 'border-red-500': errors.personal?.last_name }"
                  />
                  <p v-if="errors.personal?.last_name" class="text-red-500 text-sm mt-1">
                    {{ errors.personal.last_name }}
                  </p>
                </div>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Gender <span class="text-red-500">*</span>
                  </label>
                  <select
                    v-model="form.personal.gender"
                    required
                    class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76] dark:bg-gray-700 dark:text-gray-100"
                    :class="{ 'border-red-500': errors.personal?.gender }"
                  >
                    <option value="">Select Gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                  <p v-if="errors.personal?.gender" class="text-red-500 text-sm mt-1">
                    {{ errors.personal.gender }}
                  </p>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Date of Birth <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="form.personal.date_of_birth"
                    type="date"
                    required
                    class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76] dark:bg-gray-700 dark:text-gray-100"
                    :class="{ 'border-red-500': errors.personal?.date_of_birth }"
                  />
                  <p v-if="errors.personal?.date_of_birth" class="text-red-500 text-sm mt-1">
                    {{ errors.personal.date_of_birth }}
                  </p>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Place of Birth <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="form.personal.place_of_birth"
                    type="text"
                    required
                    class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76] dark:bg-gray-700 dark:text-gray-100"
                    :class="{ 'border-red-500': errors.personal?.place_of_birth }"
                  />
                  <p v-if="errors.personal?.place_of_birth" class="text-red-500 text-sm mt-1">
                    {{ errors.personal.place_of_birth }}
                  </p>
                </div>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Nationality <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="form.personal.nationality"
                    type="text"
                    required
                    class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76] dark:bg-gray-700 dark:text-gray-100"
                    :class="{ 'border-red-500': errors.personal?.nationality }"
                  />
                  <p v-if="errors.personal?.nationality" class="text-red-500 text-sm mt-1">
                    {{ errors.personal.nationality }}
                  </p>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Phone Number <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="form.contact.phone"
                    type="tel"
                    required
                    class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76] dark:bg-gray-700 dark:text-gray-100"
                    :class="{ 'border-red-500': errors.contact?.phone }"
                  />
                  <p v-if="errors.contact?.phone" class="text-red-500 text-sm mt-1">
                    {{ errors.contact.phone }}
                  </p>
                </div>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Email Address <span class="text-red-500">*</span>
                </label>
                <input
                  v-model="form.contact.email"
                  type="email"
                  required
                  class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76] dark:bg-gray-700 dark:text-gray-100"
                  :class="{ 'border-red-500': errors.contact?.email }"
                />
                <p v-if="errors.contact?.email" class="text-red-500 text-sm mt-1">
                  {{ errors.contact.email }}
                </p>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Residential Address <span class="text-red-500">*</span>
                </label>
                <textarea
                  v-model="form.contact.residential_address"
                  rows="3"
                  required
                  class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76] dark:bg-gray-700 dark:text-gray-100"
                  :class="{ 'border-red-500': errors.personal?.residential_address }"
                ></textarea>
                <p v-if="errors.contact?.residential_address" class="text-red-500 text-sm mt-1">
                  {{ errors.contact.residential_address }}
                </p>
              </div>
            </div>
          </section>

          <!-- 2. Marital Status & Family -->
          <section>
            <h2 class="text-xl font-semibold border-b pb-2 mb-6 text-[#035a76] dark:text-[#4db3ff]">
              Marital Status & Family Information
            </h2>
            <div class="space-y-6">
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Marital Status <span class="text-red-500">*</span>
                </label>
                <div class="space-y-2">
                  <div v-for="status in maritalStatuses" :key="status.value" class="flex items-center">
                    <input
                      :id="`marital-${status.value}`"
                      v-model="form.personal.marital_status"
                      :value="status.value"
                      type="radio"
                      required
                      class="h-4 w-4 text-[#035a76] focus:ring-[#035a76] border-gray-300 dark:border-gray-600"
                    />
                    <label :for="`marital-${status.value}`" class="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                      {{ status.label }}
                    </label>
                  </div>
                </div>
              </div>

              <div v-if="form.personal.marital_status === 'married'" class="mt-4">
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Spouse Name <span class="text-red-500">*</span>
                </label>
                <input
                  v-model="form.spouse.name"
                  type="text"
                  required
                  class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76] dark:bg-gray-700 dark:text-gray-100"
                />
              </div>

              <div class="mt-6">
                <div class="flex items-center">
                  <input
                    id="has-children"
                    v-model="form.hasChildren"
                    type="checkbox"
                    class="h-4 w-4 text-[#035a76] focus:ring-[#035a76] border-gray-300 dark:border-gray-600 rounded"
                  />
                  <label for="has-children" class="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                    Do you have any children?
                  </label>
                </div>

                <div v-if="form.hasChildren" class="mt-6 space-y-6">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Number of Children <span class="text-red-500">*</span>
                    </label>
                    <input
                      v-model.number="form.childrenCount"
                      type="number"
                      min="1"
                      max="20"
                      @change="updateChildrenFields"
                      class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76] dark:bg-gray-700 dark:text-gray-100"
                    />
                  </div>

                  <div v-for="(child, index) in form.children" :key="index" class="border p-4 rounded-md dark:border-gray-600">
                    <div class="flex justify-between items-center mb-3">
                      <h3 class="text-sm font-medium text-gray-700 dark:text-gray-300">Child {{ index + 1 }}</h3>
                      <button
                        v-if="form.children.length > 1"
                        type="button"
                        @click="removeChild(index)"
                        class="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 text-sm font-medium"
                      >
                        Remove
                      </button>
                    </div>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Full Name <span class="text-red-500">*</span>
                        </label>
                        <input
                          v-model="child.full_name"
                          type="text"
                          required
                          class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76] dark:bg-gray-700 dark:text-gray-100"
                        />
                      </div>
                      <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Age <span class="text-red-500">*</span>
                        </label>
                        <input
                          v-model.number="child.age"
                          type="number"
                          min="0"
                          max="30"
                          required
                          class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76] dark:bg-gray-700 dark:text-gray-100"
                        />
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    @click="addChild"
                    class="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-[#035a76] bg-[#76ba51]/10 hover:bg-[#76ba51]/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#035a76]"
                  >
                    <svg class="-ml-0.5 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Add Another Child
                  </button>
                </div>
              </div>

              <!-- Parents -->
              <div class="mt-8">
                <h3 class="text-lg font-medium border-b pb-2 mb-4 text-gray-800 dark:text-gray-200">
                  Parents Information
                </h3>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Father's Name <span class="text-red-500">*</span>
                    </label>
                    <input
                      v-model="form.parents.father.name"
                      type="text"
                      required
                      class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76] dark:bg-gray-700 dark:text-gray-100"
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Father's Contact <span class="text-red-500">*</span>
                    </label>
                    <input
                      v-model="form.parents.father.contact"
                      type="tel"
                      required
                      class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76] dark:bg-gray-700 dark:text-gray-100"
                    />
                  </div>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Mother's Name <span class="text-red-500">*</span>
                    </label>
                    <input
                      v-model="form.parents.mother.name"
                      type="text"
                      required
                      class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76] dark:bg-gray-700 dark:text-gray-100"
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Mother's Contact</label>
                    <input
                      v-model="form.parents.mother.contact"
                      type="tel"
                      class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76] dark:bg-gray-700 dark:text-gray-100"
                    />
                  </div>
                </div>

                <div class="mt-6">
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Parents' Address</label>
                  <textarea
                    v-model="form.parents.address"
                    rows="2"
                    class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76] dark:bg-gray-700 dark:text-gray-100"
                  ></textarea>
                </div>
              </div>
            </div>
          </section>

          <!-- 3. Work Experience -->
          <section>
            <h2 class="text-xl font-semibold border-b pb-2 mb-6 text-[#035a76] dark:text-[#4db3ff]">
              Work Experience
            </h2>
            <div v-for="(exp, index) in form.work_experience" :key="index" class="border p-4 rounded-md mb-6 dark:border-gray-600">
              <div class="flex justify-between items-center mb-4">
                <h3 class="text-sm font-medium text-gray-700 dark:text-gray-300">Previous Work {{ index + 1 }}</h3>
                <button
                  v-if="index > 0"
                  type="button"
                  @click="removeWorkExperience(index)"
                  class="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 text-sm font-medium"
                >
                  Remove
                </button>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Company Name <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="exp.company_name"
                    type="text"
                    required
                    class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76] dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Position Held <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="exp.position"
                    type="text"
                    required
                    class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76] dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Company Address <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="exp.company_address"
                    type="text"
                    required
                    class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76] dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Contact Person/Number</label>
                  <input
                    v-model="exp.contact_person_phone"
                    type="text"
                    class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76] dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Start Date <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="exp.start_date"
                    type="date"
                    required
                    class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76] dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    End Date <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="exp.end_date"
                    type="date"
                    required
                    class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76] dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
              </div>

              <div class="mt-4">
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Reason for Leaving <span class="text-red-500">*</span>
                </label>
                <textarea
                  v-model="exp.reason_for_leaving"
                  rows="2"
                  required
                  class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76] dark:bg-gray-700 dark:text-gray-100"
                ></textarea>
              </div>
            </div>

            <button
              type="button"
              @click="addWorkExperience"
              class="mt-6 inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-[#035a76] bg-[#76ba51]/10 hover:bg-[#76ba51]/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#035a76]"
            >
              <svg class="-ml-0.5 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Add Another Work Experience
            </button>
          </section>

          <!-- 4. Education -->
          <section>
            <h2 class="text-xl font-semibold border-b pb-2 mb-6 text-[#035a76] dark:text-[#4db3ff]">
              Education Background
            </h2>
            <div v-for="(edu, index) in form.education" :key="index" class="border p-4 rounded-md mb-6 dark:border-gray-600">
              <div class="flex justify-between items-center mb-4">
                <h3 class="text-sm font-medium text-gray-700 dark:text-gray-300">Education {{ index + 1 }}</h3>
                <button
                  v-if="index > 0"
                  type="button"
                  @click="removeEducation(index)"
                  class="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 text-sm font-medium"
                >
                  Remove
                </button>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Certificate/Degree <span class="text-red-500">*</span>
                  </label>
                  <select
                    v-model="edu.certificate"
                    required
                    class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76] dark:bg-gray-700 dark:text-gray-100"
                  >
                    <option value="">Select Certificate</option>
                    <option value="WASSCE">WASSCE</option>
                    <option value="Diploma">Diploma</option>
                    <option value="Bachelor">Bachelor's Degree</option>
                    <option value="Master">Master's Degree</option>
                    <option value="PhD">PhD</option>
                    <option value="Other">Other</option>
                  </select>
                  <input
                    v-if="edu.certificate === 'Other'"
                    v-model="edu.other_certificate"
                    type="text"
                    placeholder="Specify other"
                    class="mt-2 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76] dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    School Name <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="edu.institution"
                    type="text"
                    required
                    class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76] dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Start Year <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model.number="edu.start_year"
                    type="number"
                    min="1900"
                    :max="new Date().getFullYear()"
                    required
                    class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76] dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    End Year <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model.number="edu.end_year"
                    type="number"
                    min="1900"
                    :max="new Date().getFullYear()"
                    required
                    class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76] dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
              </div>
            </div>

            <button
              type="button"
              @click="addEducation"
              class="mt-6 inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-[#035a76] bg-[#035a76]/10 hover:bg-[#035a76]/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#035a76]"
            >
              <svg class="-ml-0.5 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Add Another Education
            </button>
          </section>

          <!-- 5. Emergency Contact & Bank & Security & Documents -->
          <section>
            <h2 class="text-xl font-semibold border-b pb-2 mb-6 text-[#035a76] dark:text-[#4db3ff]">
              Emergency Contact, Bank & Security
            </h2>

            <!-- Emergency Contact -->
            <div class="mb-10">
              <h3 class="text-lg font-medium mb-4 text-gray-800 dark:text-gray-200">Emergency Contact</h3>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Full Name <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="form.emergency_contact.name"
                    type="text"
                    required
                    class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76] dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Relationship <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="form.emergency_contact.relationship"
                    type="text"
                    required
                    class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76] dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Phone Number <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="form.emergency_contact.phone"
                    type="tel"
                    required
                    class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76] dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Alternative Phone</label>
                  <input
                    v-model="form.emergency_contact.alt_phone"
                    type="tel"
                    class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76] dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
              </div>

              <div class="mt-6">
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Address <span class="text-red-500">*</span>
                </label>
                <textarea
                  v-model="form.emergency_contact.address"
                  rows="2"
                  required
                  class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76] dark:bg-gray-700 dark:text-gray-100"
                ></textarea>
              </div>
            </div>

            <!-- Bank Details -->
            <div class="mb-10">
              <h3 class="text-lg font-medium mb-4 text-gray-800 dark:text-gray-200">Bank Details</h3>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Bank Name <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="form.bank_account.bank_name"
                    type="text"
                    required
                    class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76] dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Branch Name <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="form.bank_account.branch_name"
                    type="text"
                    required
                    class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76] dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Account Number <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="form.bank_account.account_number"
                    type="text"
                    required
                    class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76] dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
              </div>
              <div class="mt-6">
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Account Holder Name <span class="text-red-500">*</span>
                </label>
                <input
                  v-model="form.bank_account.account_name"
                  type="text"
                  required
                  class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76] dark:bg-gray-700 dark:text-gray-100"
                />
              </div>
            </div>

            <!-- Security / Identification -->
            <div class="mb-10">
              <h3 class="text-lg font-medium mb-4 text-gray-800 dark:text-gray-200">Identification / Security</h3>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Ghana Card Number <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="form.identifications.ghana_card_number"
                    type="text"
                    required
                    class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76] dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Ghana Card Expiry Date <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="form.identifications.ghana_card_expiry"
                    type="date"
                    required
                    class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76] dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
              </div>
              <div class="mt-6">
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  SSNIT Number <span class="text-red-500">*</span>
                </label>
                <input
                  v-model="form.identifications.ssnit_number"
                  type="text"
                  required
                  class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76] dark:bg-gray-700 dark:text-gray-100"
                />
              </div>
            </div>

            <!-- Document Uploads -->
            <div>
              <h3 class="text-lg font-medium mb-4 text-gray-800 dark:text-gray-200">Document Uploads</h3>
              <div class="space-y-6">
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Curriculum Vitae (CV) <span class="text-red-500">*</span>
                  </label>
                  <input
                    type="file"
                    @change="handleFileUpload('cv', $event)"
                    accept=".pdf,.doc,.docx"
                    required
                    class="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-[#035a76]/10 file:text-[#035a76] hover:file:bg-[#035a76]/20 dark:file:bg-gray-600 dark:file:text-gray-200"
                  />
                  <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">PDF or Word document (Max 5MB)</p>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Ghana Card (Front) <span class="text-red-500">*</span>
                    </label>
                    <input
                      type="file"
                      @change="handleFileUpload('ghanaCardFront', $event)"
                      accept="image/*,.pdf"
                      required
                      class="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-[#035a76]/10 file:text-[#035a76] hover:file:bg-[#035a76]/20 dark:file:bg-gray-600 dark:file:text-gray-200"
                    />
                    <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">Image or PDF (Max 5MB)</p>
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Ghana Card (Back) <span class="text-red-500">*</span>
                    </label>
                    <input
                      type="file"
                      @change="handleFileUpload('ghanaCardBack', $event)"
                      accept="image/*,.pdf"
                      required
                      class="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-[#035a76]/10 file:text-[#035a76] hover:file:bg-[#035a76]/20 dark:file:bg-gray-600 dark:file:text-gray-200"
                    />
                    <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">Image or PDF (Max 5MB)</p>
                  </div>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    NHIS Card <span class="text-red-500">*</span>
                  </label>
                  <input
                    type="file"
                    @change="handleFileUpload('nhisCard', $event)"
                    accept="image/*,.pdf"
                    required
                    class="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-[#035a76]/10 file:text-[#035a76] hover:file:bg-[#035a76]/20 dark:file:bg-gray-600 dark:file:text-gray-200"
                  />
                  <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">Image or PDF (Max 5MB)</p>
                </div>
              </div>
            </div>
          </section>

          <!-- 6. Signature -->
          <section>
            <h2 class="text-xl font-semibold border-b pb-2 mb-6 text-[#035a76] dark:text-[#4db3ff]">
              Employee Signature
            </h2>
            <div class="border rounded-lg p-4 bg-gray-50 dark:bg-gray-700">
              <canvas
                ref="signatureCanvas"
                class="w-full h-48 border rounded bg-white dark:bg-gray-800"
              ></canvas>
            </div>
            <div class="flex gap-3 mt-4">
              <button
                type="button"
                @click="clearSignature"
                class="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition"
              >
                Clear Signature
              </button>
            </div>
          </section>

          <!-- Save / Cancel -->
          <div class="flex justify-end gap-4 pt-8 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              @click="goBack"
              class="px-6 py-2.5 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              :disabled="saving"
              class="px-6 py-2.5 bg-[#76ba51] text-white rounded-md hover:bg-[#66a345] disabled:opacity-60 transition flex items-center gap-2"
            >
              <span v-if="saving" class="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></span>
              {{ saving ? 'Saving...' : 'Save Changes' }}
            </button>
          </div>
        </form>
      </div>
    </main>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import HrSidebar from '@/components/hr/HrSidebar.vue'
import HrTopbar from '@/components/hr/HrTopbar.vue'
import api from '@/services/api'
import SignaturePad from 'signature_pad'

const route = useRoute()
const router = useRouter()
const employeeId = route.params.id

const isSidebarOpen = ref(true)
const isDarkMode = ref(false)
const loading = ref(true)
const saving = ref(false)
const error = ref(null)

const currentPageTitle = ref('Edit Employee Profile')


const signatureCanvas = ref(null)
let signaturePad = null

const form = ref({
  personal: {},
  contact: {},
  spouse: { name: '' },
  hasChildren: false,
  childrenCount: 0,
  children: [],
  parents: {
    father: { name: '', contact: '' },
    mother: { name: '', contact: '' },
    address: ''
  },
  work_experience: [],
  education: [],
  emergency_contact: {},
  bank_account: {},
  identifications: {},
  documents: {
    cv: null,
    ghanaCardFront: null,
    ghanaCardBack: null,
    nhisCard: null
  }
})

const errors = ref({
  personal: {}
})

const maritalStatuses = [
  { value: 'single', label: 'Single' },
  { value: 'married', label: 'Married' },
  { value: 'divorced', label: 'Divorced' },
  { value: 'widowed', label: 'Widowed' }
]

const fetchEmployee = async () => {
  try {
    loading.value = true
    const res = await api.get(`get_employee_profile.php?id=${employeeId}`)
    if (res.data?.success) {
      const data = res.data
      form.value = {
        personal: { ...data.personal },
        contact: { ...data.contact },
        spouse: data.spouse ? { ...data.spouse } : { name: '' },
        hasChildren: data.children?.length > 0,
        childrenCount: data.children?.length || 0,
        children: data.children?.map(c => ({ full_name: c.full_name, age: c.age })) || [],
        parents: {
          father: { name: '', contact: '' },
          mother: { name: '', contact: '' },
          address: ''
        },
        work_experience: data.work_experience?.map(w => ({ ...w })) || [],
        education: data.education?.map(e => ({ ...e })) || [],
        emergency_contact: data.emergency_contact ? { ...data.emergency_contact } : {},
        bank_account: data.bank_account ? { ...data.bank_account } : {},
        identifications: data.identifications ? { ...data.identifications } : {},
        documents: { cv: null, ghanaCardFront: null, ghanaCardBack: null, nhisCard: null }
      }

      // Map parents if they exist in a different structure
      if (data.parents?.length) {
        data.parents.forEach(p => {
          if (p.parent_type === 'father') {
            form.value.parents.father.name = p.name
            form.value.parents.father.contact = p.contact
          } else if (p.parent_type === 'mother') {
            form.value.parents.mother.name = p.name
            form.value.parents.mother.contact = p.contact
          }
        })
        form.value.parents.address = data.parents[0]?.address || ''
      }
    }
  } catch (err) {
    console.error(err)
    error.value = 'Failed to load employee data'
  } finally {
    loading.value = false
  }
}

const updateChildrenFields = () => {
  const count = form.value.childrenCount
  while (form.value.children.length < count) {
    form.value.children.push({ full_name: '', age: '' })
  }
  while (form.value.children.length > count) {
    form.value.children.pop()
  }
}

const addChild = () => {
  form.value.children.push({ full_name: '', age: '' })
  form.value.childrenCount = form.value.children.length
}

const removeChild = (index) => {
  form.value.children.splice(index, 1)
  form.value.childrenCount = form.value.children.length
}

const addWorkExperience = () => {
  form.value.work_experience.push({
    company_name: '',
    position: '',
    company_address: '',
    contact_person_phone: '',
    start_date: '',
    end_date: '',
    reason_for_leaving: ''
  })
}

const removeWorkExperience = (index) => {
  form.value.work_experience.splice(index, 1)
}

const addEducation = () => {
  form.value.education.push({
    certificate: '',
    other_certificate: '',
    institution: '',
    start_year: '',
    end_year: ''
  })
}

const removeEducation = (index) => {
  form.value.education.splice(index, 1)
}

const handleFileUpload = (field, event) => {
  form.value.documents[field] = event.target.files[0]
}

const clearSignature = () => {
  if (signaturePad) signaturePad.clear()
}

const saveProfile = async () => {
  // Basic validation example
  errors.value.personal = {}
  let isValid = true

  if (!form.value.personal.first_name?.trim()) {
    errors.value.personal.first_name = 'First name is required'
    isValid = false
  }
  // Add more checks as needed...

  if (!isValid) {
    alert('Please fix the errors in the form')
    return
  }

  saving.value = true
  try {
    const formData = new FormData()

    // Flatten personal + add other sections
    Object.entries(form.value.personal).forEach(([k, v]) => formData.append(`personal[${k}]`, v || ''))
    if (form.value.personal.marital_status === 'married') {
      formData.append('spouse[name]', form.value.spouse.name || '')
    }
    formData.append('has_children', form.value.hasChildren ? '1' : '0')
    form.value.children.forEach((c, i) => {
      formData.append(`children[${i}][full_name]`, c.full_name || '')
      formData.append(`children[${i}][age]`, c.age || '')
    })

    // Parents
    formData.append('parents[father][name]', form.value.parents.father.name || '')
    formData.append('parents[father][contact]', form.value.parents.father.contact || '')
    formData.append('parents[mother][name]', form.value.parents.mother.name || '')
    formData.append('parents[mother][contact]', form.value.parents.mother.contact || '')
    formData.append('parents[address]', form.value.parents.address || '')

    // Work, education, etc. — similar to onboarding createFormData logic
    form.value.work_experience.forEach((w, i) => {
      Object.entries(w).forEach(([k, v]) => formData.append(`work_experience[${i}][${k}]`, v || ''))
    })
    form.value.education.forEach((e, i) => {
      Object.entries(e).forEach(([k, v]) => formData.append(`education[${i}][${k}]`, v || ''))
    })

    Object.entries(form.value.emergency_contact).forEach(([k, v]) => formData.append(`emergency_contact[${k}]`, v || ''))
    Object.entries(form.value.bank_account).forEach(([k, v]) => formData.append(`bank_account[${k}]`, v || ''))
    Object.entries(form.value.identifications).forEach(([k, v]) => formData.append(`identifications[${k}]`, v || ''))

    // Files
    Object.entries(form.value.documents).forEach(([k, file]) => {
      if (file) formData.append(`documents[${k}]`, file)
    })

    // Signature
    if (signaturePad && !signaturePad.isEmpty()) {
      const dataUrl = signaturePad.toDataURL()
      const blob = dataURLtoBlob(dataUrl)
      formData.append('signature', blob, 'signature.png')
    }

    const res = await api.post(`update_employee_profile.php?id=${employeeId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })

    if (res.data?.success) {
      alert('Profile updated successfully')
      router.push({ name: 'StaffDetails', params: { id: employeeId } })
    } else {
      alert(res.data?.message || 'Update failed')
    }
  } catch (err) {
    console.error(err)
    alert('Error saving profile')
  } finally {
    saving.value = false
  }
}

const goBack = () => router.back()

const dataURLtoBlob = (dataURL) => {
  const arr = dataURL.split(',')
  const mime = arr[0].match(/:(.*?);/)[1]
  const bstr = atob(arr[1])
  let n = bstr.length
  const u8arr = new Uint8Array(n)
  while (n--) u8arr[n] = bstr.charCodeAt(n)
  return new Blob([u8arr], { type: mime })
}

onMounted(() => {
  fetchEmployee()
  nextTick(() => {
    if (signatureCanvas.value) {
      signaturePad = new SignaturePad(signatureCanvas.value, {
        backgroundColor: 'rgb(255,255,255)'
      })
    }
  })
})
</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
</style>